import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { GraduationCap, Lock, CheckCircle2, FileUp, Mic, FileText, Save, RotateCcw, Trash2, Play, MessageSquare, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PptxBox } from "@/components/PptxBox";

const CLUBS = ["Analistas", "Diplomatas", "Sentinelas", "Exploradores"];

function authQS() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/);
    return m ? `?auth=${encodeURIComponent(m[1])}` : "";
  } catch { return ""; }
}

export default function Training() {
  const { user } = useAuth();
  const role = user?.role;
  const defaultClub = role === "coach" ? user?.club
    : role === "staff" ? user?.club
    : role === "consultant" ? null  // resolved via /captain/me
    : "Analistas";
  const [club, setClub] = useState(defaultClub || "Analistas");
  const [resolved, setResolved] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // For consultants, fetch their own club via captain/me (which returns club regardless of captain status)
  useEffect(() => {
    if (role !== "consultant") { setResolved(true); return; }
    api.get("/captain/me").then((r) => {
      if (r.data?.club) setClub(r.data.club);
      setResolved(true);
    }).catch(() => setResolved(true));
  }, [role]);

  const load = useCallback(() => api.get(`/training/${club}`).then((r) => setData(r.data)), [club]);
  useEffect(() => { if (resolved && club) load(); }, [resolved, club, load]);

  if (!resolved || !data) return <div className="kw-card p-8 text-center text-[#737373]">A carregar…</div>;

  const completedCount = data.sessions.filter((s) => s.completed).length;
  const progress = Math.round((completedCount / data.total) * 100);

  return (
    <div className="space-y-6 kw-fade-up" data-testid="training-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap className="w-5 h-5 text-[#E50000]" />
            <div className="kw-overline">Auto-Treino · 8 Sessões</div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            8 <span className="text-[#E50000]">Hábitos Enraizados</span>
          </h1>
          <p className="text-[#A3A3A3] mt-2 text-sm max-w-2xl">
            Programa de 8 semanas — <strong>O que muda daqui para a frente</strong>. Cada sessão constrói uma camada nova: identidade, bússola, proposta, negociação, escuta, comunicação, renovação e compromisso.
            Cada coach edita o conteúdo do seu clube. As sessões são <strong>sequenciais</strong> — conclui uma para desbloquear a seguinte.
          </p>
        </div>
        {(role === "admin" || (role === "staff" && !user?.club)) && (
          <div className="kw-card px-4 py-3">
            <label className="kw-overline block mb-1">Clube</label>
            <select className="kw-input" value={club} onChange={(e) => setClub(e.target.value)} data-testid="training-club-select">
              {CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
      </div>

      {role === "consultant" && (
        <div className="kw-card p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="kw-overline">Progresso · {club}</div>
            <span className="font-display text-2xl font-black text-[#E50000]" data-testid="training-progress-pct">{progress}%</span>
          </div>
          <div className="kw-funnel-bar"><span style={{ width: `${progress}%` }} /></div>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-2">{completedCount} de {data.total} sessões concluídas</div>
        </div>
      )}

      <div className="grid gap-4">
        {data.sessions.map((s) => (
          <SessionCard key={s.session_number} session={s} club={club} canEdit={data.can_edit} canPptx={data.can_upload_pptx}
            minReflChars={data.reflection_min_chars || 50} reflBonus={data.reflection_bonus_pts || 3}
            busy={busy} setBusy={setBusy} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function SessionCard({ session: s, club, canEdit, canPptx, minReflChars, reflBonus, busy, setBusy, onChanged }) {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(s.title);
  const [description, setDescription] = useState(s.description);
  const [bookRef, setBookRef] = useState(s.book_reference);
  const audioRef = useRef(null);
  const pdfRef = useRef(null);

  const locked = !s.unlocked;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/training/${club}/${s.session_number}`, { title, description, book_reference: bookRef });
      toast.success("Sessão atualizada");
      setEditing(false);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/training/${club}/${s.session_number}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind === "audio" ? "Áudio" : "PDF"} carregado`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const removeFile = async (kind) => {
    if (!window.confirm(`Remover ${kind === "audio" ? "áudio" : "PDF"} desta sessão?`)) return;
    setBusy(true);
    try {
      await api.delete(`/training/${club}/${s.session_number}/${kind}`);
      toast.success("Removido");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const toggleComplete = async () => {
    setBusy(true);
    try {
      if (s.completed) {
        await api.delete(`/training/${club}/${s.session_number}/complete`);
      } else {
        await api.post(`/training/${club}/${s.session_number}/complete`);
      }
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className={`kw-card p-5 ${locked ? "opacity-60" : ""}`} data-testid={`training-session-${s.session_number}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-12 h-12 flex items-center justify-center font-display text-2xl font-black border ${s.completed ? "bg-[#E50000] border-[#E50000] text-white" : locked ? "border-[#333] text-[#737373]" : "border-[#E50000] text-[#E50000]"}`}>
            {locked ? <Lock className="w-5 h-5" /> : s.completed ? <CheckCircle2 className="w-6 h-6" /> : s.session_number}
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <input className="kw-input mb-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`training-title-${s.session_number}`} />
            ) : (
              <h3 className="font-display text-lg font-black uppercase truncate">{s.title}</h3>
            )}
            {editing ? (
              <input className="kw-input text-xs" value={bookRef} onChange={(e) => setBookRef(e.target.value)} placeholder="Referência (ex: 7 Hábitos · Hábito 1)" data-testid={`training-bookref-${s.session_number}`} />
            ) : (
              <div className="text-[10px] uppercase tracking-widest text-[#737373] truncate">{s.book_reference}</div>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} className="kw-btn-secondary !py-1.5 !px-3 text-xs" data-testid={`training-edit-${s.session_number}`}>
              Editar
            </button>
          )}
          {canEdit && editing && (
            <>
              <button onClick={() => { setTitle(s.title); setDescription(s.description); setBookRef(s.book_reference); setEditing(false); }} className="kw-btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
              <button onClick={save} disabled={busy} className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`training-save-${s.session_number}`}>
                <Save className="w-3 h-3" /> Guardar
              </button>
            </>
          )}
        </div>
      </div>

      {locked ? (
        <p className="text-xs text-[#737373] mt-4 italic" data-testid={`training-locked-msg-${s.session_number}`}>
          {s.lock_reason === "future_week"
            ? `Disponível a partir da semana ${s.available_from_week || s.session_number}.`
            : "Conclui a sessão anterior para desbloquear."}
        </p>
      ) : (
        <>
          {editing ? (
            <textarea className="kw-input mt-4 min-h-[100px]" value={description} placeholder="Notas, objetivos e exercícios da sessão…"
              onChange={(e) => setDescription(e.target.value)} maxLength={2000} data-testid={`training-desc-${s.session_number}`} />
          ) : (
            s.description && <p className="text-sm text-[#A3A3A3] mt-4 leading-relaxed whitespace-pre-wrap">{s.description}</p>
          )}

          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-[#222]">
            {/* AUDIO */}
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-[#E50000]" />
              {s.has_audio ? (
                <>
                  <audio controls className="h-8" src={`${s.audio_url}${authQS()}`} data-testid={`training-audio-${s.session_number}`} />
                  {canEdit && (
                    <button onClick={() => removeFile("audio")} className="text-[#FF6B6B] text-xs uppercase tracking-widest font-bold" title="Remover áudio">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </>
              ) : canEdit ? (
                <>
                  <input ref={audioRef} type="file" accept="audio/*" className="hidden"
                    onChange={(e) => uploadFile("audio", e.target.files?.[0])} data-testid={`training-audio-input-${s.session_number}`} />
                  <button onClick={() => audioRef.current?.click()} disabled={busy}
                    className="kw-btn-secondary !py-1 !px-3 text-xs inline-flex items-center gap-1">
                    <FileUp className="w-3 h-3" /> Carregar áudio
                  </button>
                </>
              ) : (
                <span className="text-xs text-[#737373]">Sem áudio</span>
              )}
            </div>

            {/* PDF */}
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#E50000]" />
              {s.has_pdf ? (
                <>
                  <a href={`${s.pdf_url}${authQS()}`} target="_blank" rel="noopener noreferrer"
                    className="kw-btn-secondary !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`training-pdf-${s.session_number}`}>
                    <Play className="w-3 h-3" /> Abrir PDF
                  </a>
                  {canEdit && (
                    <button onClick={() => removeFile("pdf")} className="text-[#FF6B6B] text-xs uppercase tracking-widest font-bold" title="Remover PDF">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </>
              ) : canEdit ? (
                <>
                  <input ref={pdfRef} type="file" accept="application/pdf" className="hidden"
                    onChange={(e) => uploadFile("pdf", e.target.files?.[0])} data-testid={`training-pdf-input-${s.session_number}`} />
                  <button onClick={() => pdfRef.current?.click()} disabled={busy}
                    className="kw-btn-secondary !py-1 !px-3 text-xs inline-flex items-center gap-1">
                    <FileUp className="w-3 h-3" /> Carregar PDF
                  </button>
                </>
              ) : (
                <span className="text-xs text-[#737373]">Sem PDF</span>
              )}
            </div>

            <PptxBox s={s} club={club} canUpload={canPptx} onChanged={onChanged} />

            <div className="ml-auto">
              {!isStaff && (
                <button onClick={toggleComplete} disabled={busy}
                  className={s.completed ? "kw-btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" : "kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"}
                  data-testid={`training-complete-${s.session_number}`}>
                  {s.completed ? <><RotateCcw className="w-3 h-3" /> Reabrir</> : <><CheckCircle2 className="w-3 h-3" /> Concluir</>}
                </button>
              )}
            </div>
          </div>

          <ReflectionPanel session={s} club={club} minChars={minReflChars} bonusPts={reflBonus} onChanged={onChanged} />
        </>
      )}
    </div>
  );
}

function ReflectionPanel({ session: s, club, minChars, bonusPts, onChanged }) {
  const { user } = useAuth();
  const role = user?.role;
  const initial = s.my_reflection?.text || "";
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [list, setList] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  // Reset when session payload refreshes
  useEffect(() => { setText(s.my_reflection?.text || ""); }, [s.my_reflection?.text]);

  const credited = (s.my_reflection?.credited) || false;
  const charsNow = text.trim().length;
  const willCredit = charsNow >= minChars;
  const reflCount = s.reflection_count || 0;

  const save = async () => {
    if (!text.trim()) { toast.error("Escreve algo antes de submeter."); return; }
    setSaving(true);
    try {
      const r = await api.put(`/training/${club}/${s.session_number}/reflection`, { text });
      if (r.data?.credited) {
        toast.success(`+${bonusPts} pts creditados pela reflexão!`);
      } else {
        toast.success(`Reflexão guardada. Para ganhar +${bonusPts} pts escreve pelo menos ${minChars} caracteres.`);
      }
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const loadList = async () => {
    setLoadingList(true);
    try {
      const r = await api.get(`/training/${club}/${s.session_number}/reflections`);
      setList(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoadingList(false);
  };

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !list) loadList();
  };

  // Coach/admin: don't write reflections — only read
  const canWrite = role === "consultant" || role === "admin";

  return (
    <div className="mt-4 pt-4 border-t border-[#222]" data-testid={`reflection-panel-${s.session_number}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#3B82F6]" />
          <div className="kw-overline text-[#3B82F6]">Reflexão pós-sessão</div>
          {credited && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-[#22C55E] text-[#22C55E] inline-flex items-center gap-1" data-testid={`reflection-credited-${s.session_number}`}>
              <Sparkles className="w-3 h-3" /> +{bonusPts} pts
            </span>
          )}
        </div>
        <button onClick={toggleExpand} className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid={`reflection-toggle-${s.session_number}`}>
          {reflCount} do clube {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {canWrite && (
        <div className="mt-3 space-y-2">
          <textarea
            className="kw-input min-h-[80px] text-sm"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`O que ficou contigo? O que vais aplicar esta semana? (mín. ${minChars} caracteres para +${bonusPts} pts)`}
            maxLength={5000}
            data-testid={`reflection-input-${s.session_number}`}
          />
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">
              {charsNow}/{minChars} caracteres ·
              {willCredit ? (
                <span className="text-[#22C55E] font-bold ml-1">vai creditar +{bonusPts} pts</span>
              ) : (
                <span className="ml-1">faltam {minChars - charsNow} para creditar</span>
              )}
            </div>
            <button onClick={save} disabled={saving || !text.trim() || text === initial}
              className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
              data-testid={`reflection-save-${s.session_number}`}>
              <Save className="w-3 h-3" /> {saving ? "A guardar…" : (initial ? "Atualizar" : "Submeter")}
            </button>
          </div>
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-2" data-testid={`reflection-list-${s.session_number}`}>
          {loadingList && <p className="text-xs text-[#737373] italic">A carregar reflexões…</p>}
          {!loadingList && list && list.items.length === 0 && (
            <p className="text-xs text-[#737373] italic">Ainda sem reflexões partilhadas.</p>
          )}
          {!loadingList && list && list.items.map((r) => (
            <div key={r.id} className={`border p-3 ${r.is_mine ? "border-[#E50000]/40 bg-[#E50000]/5" : "border-[#222]"}`} data-testid={`reflection-item-${r.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                {r.photo_url ? (
                  <img src={r.photo_url} alt={r.player_name} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[10px] font-bold text-[#737373]">
                    {(r.player_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="text-xs font-semibold">{r.player_name}{r.is_mine ? " · tu" : ""}</div>
                {r.credited && (
                  <Sparkles className="w-3 h-3 text-[#22C55E]" />
                )}
              </div>
              <p className="text-xs text-[#D4D4D4] whitespace-pre-wrap leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
